<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="dashboard-main" ref="dashboardMain">
    <div class="dashboard-header" ref="dashboardHeader">
      <div class="dashboard-title"><b>Dashboard</b></div>
      <Button @click="addWidget" :disabled="dashboardLoading || !canAddWidget" class="add-widget-button">
        <img src="https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png" alt="" width="16" />
        Add Widget</Button
      >
    </div>
      <div
        ref="dashboardListWrapper"
        class="dashboard-list-wrapper"
        :class="{ 'is-empty': widgetList && widgetList.length == 0 }"
        :style="dashboardListWrapperStyle"
      >
      <div
        v-if="widgetList && widgetList.length > 0"
        class="dashboard-list"
        :style="dashboardListStyle"
      >
        <div
          v-for="widget in orderedWidgetList"
          :key="widget.id"
          :data-widget-id="widget.id"
          ref="dashboardItems"
          class="dashboard-grid-item"
          :class="{
            'dashboard-widget-add-highlight': addWidgetHighlightId === widget.id,
            'dashboard-grid-item-dragging': isDragging && dragWidgetId === widget.id,
            'dashboard-grid-item-resizing': isResizing && resizeWidgetId === widget.id,
          }"
          :style="getWidgetGridStyle(widget)"
        >
          <DashboardItem
            :widget="widget"
            :wideLayout="getWidgetWideLayout(widget)"
            @pointerdown.native.capture="clearWidgetHighlight(widget.id)"
            @save="saveWidget"
            @delete="deleteWidget"
            @widgetdragstart="onWidgetPointerStart(widget.id, $event)"
            @widgetresizestart="onWidgetResizeStart(widget.id, $event)"
            @duplicate="duplicateWidget"
            :widgetList="dashboardItemConfigList"
            :dashboardAccess="dashboardAccess"
            :dashboardSasEnabled="dashboardSasEnabled"
          />
        </div>
        <div
          key="dashboard-drag-placeholder"
          ref="dragPlaceholder"
          class="dashboard-drag-placeholder"
          v-if="isDragging && dragDropValid && dragPreviewPlacement"
          :style="dragPlaceholderStyle"
          aria-hidden="true"
        ></div>
        <div
          key="dashboard-resize-placeholder"
          ref="resizePlaceholder"
          class="dashboard-resize-placeholder"
          v-if="isResizing && resizeDropValid && resizePreviewPlacement"
          :style="resizePlaceholderStyle"
          aria-hidden="true"
        >
          <span>
            {{ resizePreviewPlacement ? `${resizePreviewPlacement.colSpan} × ${resizePreviewPlacement.rowSpan}` : '' }}
          </span>
        </div>
      </div>

      <div v-else-if="widgetList" class="dashboard-list dashboard-empty-list">
        <div class="dashboard-empty-placeholder d-flex flex-column align-items-center justify-content-start w-100 pt-5">
          <img :src="logo" />
          <div>Welcome to DSM 6G web application</div>
          <div>Please navigate yourself through the application using the left menu bar</div><br />
          <div>The dashboard is currently empty, please add a widget through the top 'Add Widget' button.</div>
        </div>
      </div>

    </div>

    <div class="dashboard-json-actions">
      <input ref="dashboardImportInput" type="file" accept="application/json,.json" @change="importDashboardJson" />
      <Button @click="openDashboardImport" :disabled="dashboardLoading" class="dashboard-json-button">
        Upload Dashboard
      </Button>
      <Button @click="exportDashboardJson" :disabled="dashboardLoading" class="dashboard-json-button">
        Download Dashboard
      </Button>
    </div>
  </div>
</template>

<script>
import DashboardItem from './DashboardItem.vue';
import dashboardItemConfig, { dashboardSasEnabled } from './dashboardItemConfig';
export default {
  components: { DashboardItem },
  computed: {
    logo() {
      return this.$store.state.loginDetails.CoLogo;
    },

    orderedWidgetList() {
      return this.widgetList || [];
    },

    currentGridLayout() {
      return this.resolveGridLayout(
        this.orderedWidgetList,
        this.getDashboardGridMetrics(),
        this.responsiveLayoutActive,
      );
    },

    activeGridLayout() {
      if (this.isDragging && this.dragPreviewLayout) return this.dragPreviewLayout;
      if (this.isResizing && this.resizePreviewLayout) return this.resizePreviewLayout;
      return this.currentGridLayout;
    },

    dashboardCanvasHeight() {
      const contentHeight = Math.max(
        this.activeGridLayout?.height || 0,
        this.widgetList?.length ? this.dashboardCanvasMinHeight : 0,
      );
      const resizeBottomMargin = this.isResizing
        ? this.getDashboardCanvasRowUnit(this.activeGridLayout?.metrics)
        : 0;
      return contentHeight > 0
        ? contentHeight + resizeBottomMargin
        : 0;
    },

    dashboardListStyle() {
      return this.dashboardCanvasHeight > 0 ? { minHeight: `${this.dashboardCanvasHeight}px` } : {};
    },

    dashboardListWrapperStyle() {
      return this.dashboardCanvasHeight > 0 ? { height: `${this.dashboardCanvasHeight}px` } : {};
    },

    dragPlaceholderStyle() {
      const placement = this.dragPreviewPlacement;
      if (!placement) return {};
      return {
        left: `${placement.left}px`,
        top: `${placement.top}px`,
        width: `${placement.width}px`,
        height: `${placement.height}px`,
      };
    },

    resizePreviewPlacement() {
      return this.resizePreviewLayout?.byId?.[this.resizeWidgetId] || null;
    },

    resizePlaceholderStyle() {
      const placement = this.resizePreviewPlacement;
      if (!placement) return {};
      return {
        left: `${placement.left}px`,
        top: `${placement.top}px`,
        width: `${placement.width}px`,
        height: `${placement.height}px`,
      };
    },

    canAddWidget() {
      return this.dashboardItemConfigList.some(
        (item) =>
          !item.disabled &&
          (!this.dashboardSasEnabled ||
            item.skipSAS ||
            this.dashboardAccess.includes(`${item.controlKey}DisableNew`) ||
            this.dashboardAccess.includes(item.controlKey)),
      );
    },

    dragPreviewPlacement() {
      return this.dragPreviewLayout?.byId?.[this.dragWidgetId] || null;
    },
  },
  data: function () {
    return {
        widgetList: null,
        dashboardLoading: false,
        saveTimer: null,
        dragWidgetId: null,
        dragPointerId: null,
        dragGridMetrics: null,
        dragInteractionBounds: null,
        dragOriginalLayout: null,
        dragOriginalPlacement: null,
        dragDropValid: false,
        dashboardLayoutWidth: 0,
        dashboardLayoutRevision: 0,
        responsiveLayoutActive: false,
        dashboardResizeObserver: null,
        dragShadowEl: null,
        resizeWidgetId: null,
        resizePointerId: null,
        resizeGridMetrics: null,
        resizeInteractionBounds: null,
        resizeOriginalPlacement: null,
        resizeOriginalLayout: null,
        resizeOriginalSpans: null,
        resizeStartScrollTop: 0,
        resizeDropValid: false,
        isResizing: false,
        resizeReleasePending: false,
        resizePreviewSpans: null,
        dashboardCanvasMinHeight: 0,
        resizeScrollFrame: null,
      scrollAnimationFrame: null,
      scrollAnimationCancel: null,
      isDragging: false,
      addWidgetHighlightId: null,
      dashboardItemConfigList: [],
      dashboardAccess: [],
      dashboardSasEnabled,
    };
  },
  methods: {
    addWidget() {
      if (this.widgetList.length >= 24) {
        this.$showAlert('error', 'Widget Limit', 'Max widget limit reached.', [{ text: 'Ok' }]);

        return;
      }

      if (this.widgetList.length > 0 && this.widgetList[this.widgetList.length - 1].new) {
        const unsavedWidgetId = this.widgetList[this.widgetList.length - 1].id;
        this.scrollToAndHighlightWidget(unsavedWidgetId);

        return;
      }

      const widgetId = crypto.randomUUID();
      this.addWidgetHighlightId = null;
      const position = this.findFreeCell(
        this.currentGridLayout.items,
        { rowSpan: 1, colSpan: 1 },
        this.getDashboardGridMetrics(),
        0,
      );
      this.widgetList.push({ id: widgetId, new: true, ...position });
      this.scrollToAndHighlightWidget(widgetId);
    },

    scrollToAndHighlightWidget(widgetId) {
      this.addWidgetHighlightId = null;
      this.$nextTick(async () => {
        const highlightWidget = () => {
          if (!this.widgetList.some((item) => item.id == widgetId)) return;

          this.addWidgetHighlightId = widgetId;
        };
        await this.scrollToLastWidget(highlightWidget);
        if (!this._isDestroyed) highlightWidget();
      });
    },

    clearWidgetHighlight(widgetId) {
      if (this.addWidgetHighlightId == widgetId) this.addWidgetHighlightId = null;
    },

    cancelWidgetScroll() {
      if (this.scrollAnimationCancel) this.scrollAnimationCancel();
    },

    scrollToLastWidget(onApproach) {
      this.cancelWidgetScroll();
      const dashboardMain = this.$refs.dashboardMain;
      const dashboardItems = this.$refs.dashboardItems || [];
      const lastWidget = dashboardItems[dashboardItems.length - 1];
      const lastItem = lastWidget?.$el || lastWidget;
      const revealOnApproach = () => {
        if (typeof onApproach == 'function') onApproach();
      };
      if (!dashboardMain || !lastItem) {
        revealOnApproach();
        return Promise.resolve();
      }

      const headerOffset = this.$refs.dashboardHeader?.offsetHeight || 0;
      const targetTop = Math.max(lastItem.offsetTop - headerOffset - 8, 0);
      if (Math.abs(dashboardMain.scrollTop - targetTop) < 2) {
        revealOnApproach();
        return Promise.resolve();
      }

      dashboardMain.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });

      return new Promise((resolve) => {
        const startedAt = Date.now();
        let revealStarted = false;
        let completed = false;
        const finish = () => {
          if (completed) return;
          completed = true;
          if (this.scrollAnimationCancel === cancel) {
            this.scrollAnimationFrame = null;
            this.scrollAnimationCancel = null;
          }
          resolve();
        };
        const cancel = () => {
          if (completed) return;
          if (this.scrollAnimationCancel === cancel && this.scrollAnimationFrame) {
            cancelAnimationFrame(this.scrollAnimationFrame);
          }
          finish();
        };
        const checkScroll = () => {
          if (completed) return;
          const distanceToTarget = Math.abs(dashboardMain.scrollTop - targetTop);
          const reachedTarget = distanceToTarget < 2;
          const approachingTarget = distanceToTarget <= 80;
          const timedOut = Date.now() - startedAt >= 500;
          if (!revealStarted && (approachingTarget || timedOut)) {
            revealStarted = true;
            revealOnApproach();
          }
          if (reachedTarget || timedOut) {
            finish();
            return;
          }
          this.scrollAnimationFrame = window.requestAnimationFrame(checkScroll);
        };
        this.scrollAnimationCancel = cancel;
        this.scrollAnimationFrame = window.requestAnimationFrame(checkScroll);
      });
    },

    async deleteWidget(id, newWidget = false) {
      if (!newWidget) {
        const discard = await this.$showAlert(
          'warning',
          'Remove Widget',
          'Are you sure you want to remove this widget?',
          [{ text: 'Remove', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
        );

        if (discard == 'Cancel') return false;
      }
      this.widgetList = this.widgetList.filter((item) => item.id != id);
      this.compactWidgetCoordinates();
      this.saveDashboard();
    },

    saveWidget(data) {
      const widgetListCleaned = this.widgetList
        .filter((item) => !item.new)
        // eslint-disable-next-line no-unused-vars
        .map(({ id, new: _new, ...rest }) => rest);
      const widgetListSerialized = JSON.stringify(widgetListCleaned);

      if (this.widgetList.length >= 24) {
        this.$showAlert('error', 'Widget Limit', 'Max widget limit reached.', [{ text: 'Ok' }]);

        return;
      }

      if (widgetListSerialized.length > 8000) {
        this.$showAlert('error', 'Widget Limit', 'Max widget save file reached.', [{ text: 'Ok' }]);
        return;
      }
      const { id } = data || {};
      this.widgetList = this.widgetList.map((item) => (item.id == id ? { ...item, ...data, new: false } : item));
      this.saveDashboard();
    },

    async duplicateWidget(widget) {
      const discard = await this.$showAlert(
        'warning',
        'Duplicate Widget',
        'Are you sure you want to duplicate this widget?',
        [{ text: 'Duplicate', color: '#6c8bfd' }, { text: 'Cancel' }],
      );

      if (discard == 'Cancel') return false;

      const widgetListCleaned = [...this.widgetList, widget].filter((item) => !item.new).map(
        // eslint-disable-next-line no-unused-vars
        ({ id, new: _new, ...rest }) => rest,
      );
      const widgetListSerialized = JSON.stringify(widgetListCleaned);

      if (widgetListSerialized.length > 8000) {
        this.$showAlert('error', 'Widget Limit', 'Max widget limit reached.', [{ text: 'Ok' }]);
        return;
      }
      if (this.widgetList.length >= 24) {
        this.$showAlert('error', 'Widget Limit', 'Max widget limit reached.', [{ text: 'Ok' }]);
        return;
      }

      // eslint-disable-next-line no-unused-vars
      const { id, new: _new, ...rest } = widget;
      const duplicate = {
        ...rest,
        id: crypto.randomUUID(),
        ...this.findFreeCell(this.currentGridLayout.items, rest, this.getDashboardGridMetrics(), 0),
      };
      this.widgetList = [...this.widgetList, duplicate];

      this.saveDashboard();
    },

    normalizeWidgetRowSpan(value) {
      return Number(value) == 2 ? 2 : 1;
    },

    normalizeWidgetColSpan(value) {
      return Number(value) == 2 ? 2 : 1;
    },

    getDashboardGridMetrics(listEl = this.getDashboardListElement()) {
      const layoutRevision = this.dashboardLayoutRevision;
      const dashboardLayoutWidth = this.dashboardLayoutWidth;
      const styles = listEl ? window.getComputedStyle(listEl) : null;
      const rowHeight = parseFloat(styles?.gridAutoRows) || 0;
      const columnGap = parseFloat(styles?.columnGap) || 0;
      const rowGap = parseFloat(styles?.rowGap) || columnGap;
      const columns = Math.max(styles?.gridTemplateColumns?.split(/\s+/).filter(Boolean).length || 1, 1);
      const listWidth = listEl?.clientWidth || dashboardLayoutWidth || 0;
      const columnWidth = Math.max(0, (listWidth - columnGap * (columns - 1)) / columns);
      return { columns, columnGap, rowGap, rowHeight, columnWidth, layoutRevision };
    },

    hasValidGridCoordinates(widget) {
      return Number.isInteger(Number(widget?.gridRow)) && Number(widget.gridRow) >= 0 &&
        Number.isInteger(Number(widget?.gridColumn)) && Number(widget.gridColumn) >= 0;
    },

    normalizeGridCoordinate(value) {
      const number = Number(value);
      return Number.isInteger(number) && number >= 0 ? number : 0;
    },

    getPlacementGeometry(item, metrics, effectiveColSpan = null) {
      const rowSpan = this.normalizeWidgetRowSpan(item?.rowSpan);
      const colSpan = Math.min(
        effectiveColSpan == null ? this.normalizeWidgetColSpan(item?.colSpan) : effectiveColSpan,
        Math.max(metrics?.columns || 1, 1),
      );
      const gridRow = this.normalizeGridCoordinate(item?.gridRow);
      const gridColumn = this.normalizeGridCoordinate(item?.gridColumn);
      const width = metrics.columnWidth * colSpan + Math.max(0, colSpan - 1) * metrics.columnGap;
      const height = metrics.rowHeight * rowSpan + Math.max(0, rowSpan - 1) * metrics.rowGap;
      const left = gridColumn * (metrics.columnWidth + metrics.columnGap);
      const top = gridRow * (metrics.rowHeight + metrics.rowGap);
      return {
        ...item,
        gridRow,
        gridColumn,
        rowSpan,
        colSpan,
        left,
        top,
        right: left + width,
        bottom: top + height,
        width,
        height,
      };
    },

    cellsOverlap(a, b) {
      return (
        a.gridColumn < b.gridColumn + b.colSpan &&
        a.gridColumn + a.colSpan > b.gridColumn &&
        a.gridRow < b.gridRow + b.rowSpan &&
        a.gridRow + a.rowSpan > b.gridRow
      );
    },

    canPlaceAt(items, candidate, metrics, ignoreId = null) {
      const columns = Math.max(metrics?.columns || 1, 1);
      if (candidate.gridColumn < 0 || candidate.gridRow < 0 || candidate.gridColumn + candidate.colSpan > columns) {
        return false;
      }
      return !items.some((item) => {
        if (!item || item.id == ignoreId) return false;
        const placement = this.getPlacementGeometry(item, metrics, Math.min(this.normalizeWidgetColSpan(item.colSpan), columns));
        return this.cellsOverlap(candidate, placement);
      });
    },

    findFreeCell(items, shape, metrics, startRow = 0, ignoreId = null, startColumn = 0) {
      const columns = Math.max(metrics?.columns || 1, 1);
      const rowSpan = this.normalizeWidgetRowSpan(shape?.rowSpan);
      const colSpan = Math.min(this.normalizeWidgetColSpan(shape?.colSpan), columns);
      const firstRow = Math.max(0, startRow);
      const maxSearchRows = Math.max(64, items.length * 4 + 8);
      for (let rowOffset = 0; rowOffset <= maxSearchRows; rowOffset++) {
        const row = firstRow + rowOffset;
        const firstColumn = rowOffset == 0 ? Math.max(0, startColumn) : 0;
        for (let column = firstColumn; column <= columns - colSpan; column++) {
          const candidate = { gridRow: row, gridColumn: column, rowSpan, colSpan };
          if (this.canPlaceAt(items, candidate, metrics, ignoreId)) return { gridRow: row, gridColumn: column };
        }
      }
      return { gridRow: firstRow + maxSearchRows + 1, gridColumn: 0 };
    },

    compactResolvedLayout(items = [], metrics = this.getDashboardGridMetrics(), pinnedId = null) {
      const columns = Math.max(metrics?.columns || 1, 1);
      const normalized = items.map((item) => {
        const colSpan = Math.min(this.normalizeWidgetColSpan(item.colSpan), columns);
        return {
          ...item,
          gridRow: this.normalizeGridCoordinate(item.gridRow),
          gridColumn: Math.min(this.normalizeGridCoordinate(item.gridColumn), Math.max(0, columns - colSpan)),
          rowSpan: this.normalizeWidgetRowSpan(item.rowSpan),
          colSpan,
        };
      });
      const pinned = normalized.find((item) => item.id == pinnedId);
      const placed = pinned ? [{ ...pinned }] : [];
      const ordered = normalized
        .filter((item) => !pinned || item.id != pinned.id)
        .sort((a, b) => a.gridRow - b.gridRow || a.gridColumn - b.gridColumn || String(a.id).localeCompare(String(b.id)));
      ordered.forEach((item) => {
        const shape = {
          ...item,
          gridRow: 0,
          gridColumn: 0,
        };
        const position = this.findFreeCell(placed, shape, metrics, 0, item.id, 0);
        placed.push({ ...item, ...position });
      });
      return placed;
    },

    resolveGridLayout(items = [], metrics = this.getDashboardGridMetrics(), compact = false) {
      return this.applyResolvedLayoutToWidgets(items, metrics, compact).layout;
    },

    applyResolvedLayoutToWidgets(items = [], metrics = this.getDashboardGridMetrics(), compact = false) {
      const columns = Math.max(metrics?.columns || 1, 1);
      const working = items.map((item) => ({
        ...item,
        gridRow: this.normalizeGridCoordinate(item.gridRow),
        gridColumn: this.normalizeGridCoordinate(item.gridColumn),
        rowSpan: this.normalizeWidgetRowSpan(item.rowSpan),
        colSpan: this.normalizeWidgetColSpan(item.colSpan),
      }));
      const ordered = [...working].sort((a, b) => {
        const rowDifference = a.gridRow - b.gridRow;
        if (rowDifference) return rowDifference;
        const columnDifference = a.gridColumn - b.gridColumn;
        if (columnDifference) return columnDifference;
        return String(a.id || '').localeCompare(String(b.id || ''));
      });
      const resolved = [];
      ordered.forEach((item) => {
        const effective = this.getPlacementGeometry(item, metrics, Math.min(item.colSpan, columns));
        let candidate = {
          gridRow: effective.gridRow,
          gridColumn: Math.min(effective.gridColumn, Math.max(0, columns - effective.colSpan)),
          rowSpan: effective.rowSpan,
          colSpan: effective.colSpan,
        };
        if (!this.canPlaceAt(resolved, candidate, metrics, item.id)) {
          const free = this.findFreeCell(
            resolved,
            candidate,
            metrics,
            candidate.gridRow,
            item.id,
            candidate.gridColumn,
          );
          candidate = { ...candidate, ...free };
        }
        resolved.push({ ...item, ...candidate });
      });
      const laidOut = compact ? this.compactResolvedLayout(resolved, metrics) : resolved;
      const placements = laidOut.map((item) => this.getPlacementGeometry(item, metrics));
      const byId = placements.reduce((acc, item) => {
        if (item?.id) acc[item.id] = item;
        return acc;
      }, {});
      const height = placements.reduce((max, item) => Math.max(max, item.bottom), 0);
      return {
        widgets: items.map((item) => byId[item.id] || item),
        layout: { items: placements, byId, metrics, height },
      };
    },

    compactWidgetCoordinates() {
      if (!this.widgetList?.length) return false;
      const layout = this.applyResolvedLayoutToWidgets(
        this.widgetList,
        this.getDashboardGridMetrics(),
        true,
      ).layout;
      const nextWidgets = this.widgetList.map((widget) => {
        const placement = layout.byId[widget.id];
        if (!placement || (widget.gridRow == placement.gridRow && widget.gridColumn == placement.gridColumn)) {
          return widget;
        }
        return { ...widget, gridRow: placement.gridRow, gridColumn: placement.gridColumn };
      });
      const changed = nextWidgets.some(
        (widget, index) =>
          widget.gridRow != this.widgetList[index].gridRow ||
          widget.gridColumn != this.widgetList[index].gridColumn,
      );
      if (changed) {
        this.widgetList = nextWidgets;
        this.$store.commit('updateDashboard', this.widgetList);
      }
      return changed;
    },

    buildCoordinateLayout(items = [], metrics = this.getDashboardGridMetrics(), metadata = {}) {
      const placements = items.map((item) => this.getPlacementGeometry(item, metrics));
      const byId = placements.reduce((acc, item) => {
        if (item?.id) acc[item.id] = item;
        return acc;
      }, {});
      const height = placements.reduce((max, item) => Math.max(max, item.bottom), 0);
      return { items: placements, byId, metrics, height, ...metadata };
    },

    migrateLegacyCoordinates(items = []) {
      const legacyMetrics = { columns: 4, columnGap: 0, rowGap: 0, rowHeight: 1, columnWidth: 1 };
      const existing = items.filter((item) => this.hasValidGridCoordinates(item));
      const missing = items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !this.hasValidGridCoordinates(item))
        .sort((a, b) => {
          const aOrder = Number(a.item?.gridOrder);
          const bOrder = Number(b.item?.gridOrder);
          const aHasOrder = Number.isFinite(aOrder);
          const bHasOrder = Number.isFinite(bOrder);
          if (aHasOrder && bHasOrder && aOrder != bOrder) return aOrder - bOrder;
          if (aHasOrder != bHasOrder) return aHasOrder ? -1 : 1;
          return a.index - b.index;
        });
      if (!missing.length) return { widgets: items, migrated: false };

      const migrated = [...existing];
      missing.forEach(({ item }) => {
        const position = this.findFreeCell(migrated, item, legacyMetrics, 0);
        migrated.push({ ...item, ...position });
      });
      const byId = migrated.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
      return { widgets: items.map((item) => byId[item.id]), migrated: true };
    },

    getWidgetGridStyle(widget) {
      const placement = this.activeGridLayout.byId[widget?.id];
      if (!placement) return {};
      return {
        position: 'absolute',
        left: `${placement.left}px`,
        top: `${placement.top}px`,
        width: `${placement.width}px`,
        height: `${placement.height}px`,
      };
    },

    getWidgetWideLayout(widget) {
      if (!widget?.id) return false;
      let colSpan = this.activeGridLayout.byId[widget.id]?.colSpan;
      if (this.isResizing && this.resizeWidgetId == widget.id && this.resizePreviewSpans) {
        colSpan = Math.min(
          this.resizePreviewSpans.colSpan,
          Math.max(this.resizeGridMetrics?.columns || 1, 1),
        );
      }
      return colSpan == 2;
    },

    getDashboardSaveJson(widgets = this.widgetList || []) {
      return widgets.filter((item) => !item.new).map(
        // eslint-disable-next-line no-unused-vars
        ({ id, new: _new, ...rest }) => rest,
      );
    },

    exportDashboardJson() {
      const dashboardJson = JSON.stringify(this.getDashboardSaveJson(), null, 2);
      const blob = new Blob([dashboardJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dashboard-${new Date().formatString('yyyyMMdd-HHmmss')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    openDashboardImport() {
      this.$refs.dashboardImportInput.value = '';
      this.$refs.dashboardImportInput.click();
    },
    async importDashboardJson(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        const importedWidgets = JSON.parse(text);
        if (!Array.isArray(importedWidgets)) throw new Error('Dashboard JSON must be an array.');

        const discard = await this.$showAlert(
          'warning',
          'Import Dashboard',
          'Importing this file will replace the current dashboard.',
          [{ text: 'Import', color: '#6c8bfd' }, { text: 'Cancel' }],
        );
        if (discard == 'Cancel') return;

        const importedWithIds = importedWidgets.map((item) => ({
          ...item,
          id: crypto.randomUUID(),
        }));
        const migration = this.migrateLegacyCoordinates(importedWithIds);
        this.widgetList = migration.widgets;
        this.compactWidgetCoordinates();
        this.saveDashboard();
      } catch (err) {
        this.$showAlert('error', 'Import Dashboard Failed', err.message || 'Invalid dashboard JSON.', [{ text: 'Ok' }]);
      }
    },

    saveDashboard(immediate = false) {
      this.$store.commit('updateDashboard', this.widgetList || []);

      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      const persist = () => {
        const widgetListSerialized = JSON.stringify(this.getDashboardSaveJson());
        return this.$apiCallV2('/Dashboard/SaveDashboard', { settingsvalue: widgetListSerialized }, 'POST', {
          message: `Save Dashboard failed`,
        });
      };
      if (immediate) return persist();

      this.saveTimer = setTimeout(() => {
        this.saveTimer = null;
        persist();
      }, 500);
    },

    async getDashboard() {
      this.dashboardLoading = true;
      if (this._isDestroyed) return;
      const result = await this.$apiCallV2('/Dashboard/GetDashboard', {}, 'POST', {
        message: `Get Dashboard failed`,
      });
      if (this._isDestroyed) return;
      const resultSerialized = result[0]?.SettingsValue ? JSON.parse(result[0].SettingsValue) : [];
      const widgetListAddID = resultSerialized.map((item) => ({ ...item, id: crypto.randomUUID() }));
      const migration = this.migrateLegacyCoordinates(widgetListAddID);
      this.widgetList = migration.widgets;
      this.$store.commit('updateDashboard', this.widgetList);
      this.dashboardLoading = false;
      if (migration.migrated) this.saveDashboard();
    },

    handleUnload() {
      this.flushPendingReleaseCommit(true);
      if (this.saveTimer) this.saveDashboard(true);
    },
    getDashboardListElement() {
      return this.$refs.dashboardMain?.querySelector('.dashboard-list');
    },
    getDashboardInteractionBounds() {
      const listRect = this.getDashboardListElement()?.getBoundingClientRect();
      const mainRect = this.$refs.dashboardMain?.getBoundingClientRect();
      if (!listRect || !mainRect) return null;
      return {
        listLeft: listRect.left,
        listRight: listRect.right,
        listTop: listRect.top,
        mainTop: mainRect.top,
        mainBottom: mainRect.bottom,
        scrollTop: this.$refs.dashboardMain?.scrollTop || 0,
      };
    },
    getInteractionListTop(bounds) {
      if (!bounds) return 0;
      const scrollTop = this.$refs.dashboardMain?.scrollTop || 0;
      return bounds.listTop - (scrollTop - bounds.scrollTop);
    },
    handleDashboardResize(event) {
      const listElement = this.getDashboardListElement();
      const nextWidth = listElement?.clientWidth ||
        listElement?.getBoundingClientRect()?.width ||
        this.dashboardLayoutWidth || 0;
      const isVisualViewportResize = event?.visualViewport === true ||
        event?.currentTarget == window.visualViewport ||
        event?.target == window.visualViewport;
      if (!isVisualViewportResize && nextWidth == this.dashboardLayoutWidth) return;
      this.dashboardLayoutRevision += 1;
      this.dashboardLayoutWidth = nextWidth;
      this.responsiveLayoutActive = true;
      if (this.isDragging) {
        this.dragGridMetrics = this.getDashboardGridMetrics();
        this.dragInteractionBounds = this.getDashboardInteractionBounds();
        if (this.dragWidgetId && this.dragOriginalLayout) {
          this.dragOriginalLayout = this.buildCoordinateLayout(
            this.currentGridLayout.items.map((item) => ({ ...item })),
            this.dragGridMetrics,
          );
          this.dragOriginalPlacement = this.dragOriginalLayout.byId[this.dragWidgetId];
        }
      }
      if (this.isResizing) {
        this.resizeGridMetrics = this.getDashboardGridMetrics();
        this.resizeInteractionBounds = this.getDashboardInteractionBounds();
        if (this.resizeWidgetId && this.resizeOriginalLayout) {
          this.resizeOriginalLayout = this.buildCoordinateLayout(
            this.currentGridLayout.items.map((item) => ({ ...item })),
            this.resizeGridMetrics,
          );
          this.resizeOriginalPlacement = this.resizeOriginalLayout.byId[this.resizeWidgetId];
        }
      }
      if (this.isDragging && this.dragPointerPoint) this.updateDragTargetFromPointer();
      if (this.isResizing && this.resizePointerPoint) this.updateResizeFromPointer();
    },
    handleVisualViewportResize() {
      this.handleDashboardResize({ visualViewport: true });
    },
    getDashboardItemElements() {
      const items = this.$refs.dashboardItems || [];
      return (Array.isArray(items) ? items : [items])
        .map((item) => item?.$el || item)
        .filter((item) => item?.nodeType == 1);
    },
    cacheDashboardItemElements() {
      this._dashboardItemElementsById = this.getDashboardItemElements().reduce((elements, element) => {
        if (element.dataset.widgetId) elements[element.dataset.widgetId] = element;
        return elements;
      }, {});
      return this._dashboardItemElementsById;
    },
    getDashboardItemElementMap() {
      return this._dashboardItemElementsById || this.cacheDashboardItemElements();
    },
    captureWidgetPointer(event, targetOverride = null) {
      const target = targetOverride || event?.target;
      if (!target || event.pointerId == null || typeof target.setPointerCapture != 'function') return null;
      try {
        target.setPointerCapture(event.pointerId);
        return target;
      } catch (error) {
        return null;
      }
    },
    releaseWidgetPointer(target, pointerId) {
      if (!target || pointerId == null || typeof target.releasePointerCapture != 'function') return;
      try {
        target.releasePointerCapture(pointerId);
      } catch (error) {
        // Pointer capture may already be released by the browser.
      }
    },
    getDragPlaceholderElement() {
      const element = this.$refs.dragPlaceholder;
      return Array.isArray(element) ? element[0] : element;
    },
    getDashboardCanvasRowUnit(metrics = null) {
      const rowHeight = Number(metrics?.rowHeight) || 0;
      const rowGap = Math.max(0, Number(metrics?.rowGap) || 0);
      return rowHeight > 0 ? rowHeight + rowGap : 0;
    },
    syncDashboardCanvasHeight(height, metrics = null) {
      const nextHeight = Number(height) > 0 ? height : 0;
      const resizeBottomMargin = this.isResizing
        ? this.getDashboardCanvasRowUnit(metrics || this.activeGridLayout?.metrics)
        : 0;
      const renderedHeight = nextHeight > 0
        ? nextHeight + resizeBottomMargin
        : 0;
      const value = renderedHeight > 0 ? `${renderedHeight}px` : '';
      const wrapperElement = this.$refs.dashboardListWrapper;
      const listElement = this.getDashboardListElement();
      if (
        this.dashboardCanvasMinHeight == nextHeight &&
        (!wrapperElement || wrapperElement.style.height == value) &&
        (!listElement || listElement.style.minHeight == value)
      ) {
        return;
      }
      this.dashboardCanvasMinHeight = nextHeight;
      if (wrapperElement) wrapperElement.style.height = value;
      if (listElement) listElement.style.minHeight = value;
    },
    resetResizeInteraction(finalLayout) {
      this.resizeWidgetId = null;
      this.resizePointerId = null;
      this.resizePointerCaptureTarget = null;
      this.resizePointerPoint = null;
      this.resizeStartPoint = null;
      this.resizeStartScrollTop = 0;
      this.resizeGridMetrics = null;
      this.resizeInteractionBounds = null;
      this.resizePreviewLayout = null;
      this.resizeOriginalPlacement = null;
      this.resizeOriginalLayout = null;
      this.resizeOriginalSpans = null;
      this.resizePreviewSpans = null;
      this.resizeDropValid = false;
      this.syncDashboardCanvasHeight(finalLayout?.height, finalLayout?.metrics);
      this.isResizing = false;
      this.resizeReleasePending = false;
      this.cancelResizeGhostAnimation();
      this._resizeGhostSize = null;
      this._dashboardItemElementsById = null;
      this._previewAppliedLayout = null;
    },
    resetDragInteraction(finalLayout) {
      this.dragWidgetId = null;
      this.dragPointerId = null;
      this.dragPointerCaptureTarget = null;
      this.dragPointerPoint = null;
      this.dragGridMetrics = null;
      this.dragInteractionBounds = null;
      this.dragOriginalLayout = null;
      this.dragPreviewLayout = null;
      this.dragOriginalPlacement = null;
      this.dragTargetCell = null;
      this.dragDropValid = false;
      this.syncDashboardCanvasHeight(finalLayout?.height, finalLayout?.metrics);
      this.isDragging = false;
      this._dashboardItemElementsById = null;
      this._previewAppliedLayout = null;
      this.removeDragShadow();
      if (this.dragScrollFrame) {
        cancelAnimationFrame(this.dragScrollFrame);
        this.dragScrollFrame = null;
      }
    },
    cancelPendingReleaseCommit() {
      if (this._releaseCommitFrame) cancelAnimationFrame(this._releaseCommitFrame);
      if (this._releaseCommitTimer) clearTimeout(this._releaseCommitTimer);
      this._releaseCommitFrame = null;
      this._releaseCommitTimer = null;
      this._releaseCommitPending = null;
    },
    completeReleaseCommit(pending, saveImmediately = false) {
      const deferResizeTeardown = pending.kind == 'resize' && !saveImmediately;
      this.commitPreviewLayout(
        pending.layout,
        pending.activeWidgetId,
        pending.commitSpans,
        pending.spans,
      );
      const finish = () => {
        if (pending.kind == 'resize') this.resetResizeInteraction(pending.layout);
        else this.resetDragInteraction(pending.layout);
        if (saveImmediately) this.saveDashboard(true);
        else this.saveDashboard();
      };
      if (deferResizeTeardown) {
        this.$nextTick(() => {
          if (this._isDestroyed || !this.isResizing) return;
          requestAnimationFrame(() => {
            if (this._isDestroyed || !this.isResizing) return;
            finish();
          });
        });
        return;
      }
      finish();
    },
    flushPendingReleaseCommit(saveImmediately = false) {
      const pending = this._releaseCommitPending;
      if (!pending) return false;
      this.cancelPendingReleaseCommit();
      this.completeReleaseCommit(pending, saveImmediately);
      return true;
    },
    scheduleReleaseCommit(pending) {
      this.flushPendingReleaseCommit();
      this._releaseCommitPending = pending;
      const commitWhenReady = () => {
        this._releaseCommitFrame = null;
        if (this._releaseCommitPending !== pending || this._isDestroyed) return;
        if (
          pending.kind == 'resize' &&
          (this._layoutAnimationFrame || this._layoutAnimationItems.length)
        ) {
          this._releaseCommitFrame = requestAnimationFrame(commitWhenReady);
          return;
        }
        if (pending.kind == 'resize') {
          this._releaseCommitFrame = requestAnimationFrame(() => {
            this._releaseCommitFrame = null;
            if (this._releaseCommitPending !== pending || this._isDestroyed) return;
            this._releaseCommitTimer = setTimeout(() => {
              this._releaseCommitTimer = null;
              if (this._releaseCommitPending !== pending || this._isDestroyed) return;
              this._releaseCommitPending = null;
              this.completeReleaseCommit(pending);
            }, 0);
          });
          return;
        }
        this._releaseCommitPending = null;
        this.completeReleaseCommit(pending);
      };
      this._releaseCommitFrame = requestAnimationFrame(commitWhenReady);
    },
    applyPreviewLayoutToDom(layout, activeWidgetId, placeholderType) {
      if (!layout) return;
      const elementsById = this.getDashboardItemElementMap();
      const previousLayout = this._previewAppliedLayout;
      const affectedIds = new Set(previousLayout ? [] : layout.items.map((item) => item.id));
      (layout.affectedIds || []).forEach((id) => affectedIds.add(id));
      if (previousLayout) {
        layout.items.forEach((item) => {
          const previous = previousLayout.byId[item.id];
          if (
            !previous ||
            previous.left != item.left ||
            previous.top != item.top ||
            previous.width != item.width ||
            previous.height != item.height
          ) {
            affectedIds.add(item.id);
          }
        });
      }
      if (activeWidgetId) affectedIds.add(activeWidgetId);
      affectedIds.forEach((id) => {
        const placement = layout.byId[id];
        const element = elementsById[id];
        if (!placement || !element) return;
        element.style.position = 'absolute';
        element.style.left = `${placement.left}px`;
        element.style.top = `${placement.top}px`;
        element.style.width = `${placement.width}px`;
        element.style.height = `${placement.height}px`;
      });

      const placement = layout.byId[activeWidgetId];
      const placeholder = placeholderType == 'resize' ? this.getResizeGhostElement() : this.getDragPlaceholderElement();
      const updatePlaceholder = () => {
        if (!placement || !placeholder) return;
        placeholder.style.left = `${placement.left}px`;
        placeholder.style.top = `${placement.top}px`;
        placeholder.style.width = `${placement.width}px`;
        placeholder.style.height = `${placement.height}px`;
        if (placeholderType == 'resize') {
          const label = placeholder.querySelector('span');
          if (label) label.textContent = `${placement.colSpan} × ${placement.rowSpan}`;
        }
      };
      if (placeholder) {
        updatePlaceholder();
      } else if (
        (placeholderType == 'resize' ? this.isResizing : this.isDragging) &&
        !this._previewDomTickPending
      ) {
        this._previewDomTickPending = true;
        this.$nextTick(() => {
          this._previewDomTickPending = false;
          if (placeholderType == 'resize' ? !this.isResizing : !this.isDragging) return;
          const currentLayout = placeholderType == 'resize' ? this.resizePreviewLayout : this.dragPreviewLayout;
          this.applyPreviewLayoutToDom(currentLayout, activeWidgetId, placeholderType);
        });
      }
      const height = Math.max(
        this.dragOriginalLayout?.height || 0,
        layout.height || 0,
        placeholderType == 'drag' && placement && this.dragGridMetrics
          ? placement.bottom
          : 0,
        placeholderType == 'resize' && this.resizePointerPoint && this.resizeGridMetrics
          ? this.getResizePointerCanvasY()
          : 0,
      );
      this.syncDashboardCanvasHeight(height, layout.metrics);
      this._previewAppliedLayout = layout;
    },
    getResizePointerCanvasY() {
      const point = this.resizePointerPoint;
      const bounds = this.resizeInteractionBounds || this.getDashboardInteractionBounds();
      const container = this.$refs.dashboardMain;
      if (!point || !bounds || !container) return 0;
      const listTop = this.getInteractionListTop(bounds);
      return Math.max(0, point.y - listTop + container.scrollTop);
    },
    startWidgetDrag(widgetId, event) {
      if (
        !event ||
        this.isDragging ||
        this.isResizing ||
        (event.pointerType == 'mouse' && event.button != 0)
      ) return false;
      this.flushPendingReleaseCommit();
      if (event.cancelable) event.preventDefault();
      if (window.getSelection().toString()) window.getSelection().removeAllRanges();
      this.dragGridMetrics = this.getDashboardGridMetrics();
      this.dragInteractionBounds = this.getDashboardInteractionBounds();
      if (!this.dragInteractionBounds) return false;
      this.dragOriginalLayout = this.buildCoordinateLayout(
        this.currentGridLayout.items.map((item) => ({ ...item })),
        this.dragGridMetrics,
      );
      this.cacheDashboardItemElements();
      this.dragOriginalPlacement = this.dragOriginalLayout.byId[widgetId];
      if (!this.dragOriginalPlacement) return false;
      this.dragWidgetId = widgetId;
      this.dragPointerId = event.pointerId;
      this.dragPointerCaptureTarget = this.captureWidgetPointer(event);
      this.isDragging = true;
      this.createDragShadow(widgetId, event);
      this.updateDragPosition(event);
      return true;
    },
    onWidgetPointerStart(widgetId, event) {
      if (!this.startWidgetDrag(widgetId, event)) return;
      window.addEventListener('pointermove', this.onWidgetPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onWidgetPointerEnd);
      window.addEventListener('pointercancel', this.onWidgetPointerCancel);
      window.addEventListener('keydown', this.onDashboardKeyDown);
      window.addEventListener('blur', this.onDashboardWindowBlur);
    },
    getDragPreviewLayout(targetCell) {
      if (!this.dragOriginalLayout || !targetCell || !this.dragOriginalPlacement) return null;
      const dragged = {
        ...this.dragOriginalPlacement,
        gridRow: targetCell.row,
        gridColumn: targetCell.column,
      };
      const compacted = this.compactResolvedLayout(
        [
          dragged,
          ...this.dragOriginalLayout.items.filter((item) => item.id != this.dragWidgetId),
        ],
        this.dragGridMetrics,
        this.dragWidgetId,
      );
      const affectedIds = compacted
        .filter((item) => {
          const original = this.dragOriginalLayout.items.find((candidate) => candidate.id == item.id);
          return (
            original &&
            (original.gridRow != item.gridRow || original.gridColumn != item.gridColumn)
          );
        })
        .map((item) => item.id);
      return this.buildCoordinateLayout(compacted, this.dragGridMetrics, { affectedIds });
    },
    getGridCellFromPoint(clientX, clientY) {
      const bounds = this.dragInteractionBounds || this.getDashboardInteractionBounds();
      const metrics = this.dragGridMetrics || this.getDashboardGridMetrics();
      const listTop = this.getInteractionListTop(bounds);
      if (
        !bounds ||
        clientX < bounds.listLeft ||
        clientX > bounds.listRight ||
        clientY < listTop ||
        clientY > bounds.mainBottom
      ) {
        return null;
      }
      const x = clientX - bounds.listLeft;
      const y = clientY - listTop;
      const columnPitch = metrics.columnWidth + metrics.columnGap;
      const rowPitch = metrics.rowHeight + metrics.rowGap;
      if (columnPitch <= 0 || rowPitch <= 0) return null;
      const column = Math.floor(x / columnPitch);
      const row = Math.floor(y / rowPitch);
      const colSpan = Math.min(this.dragOriginalPlacement?.colSpan || 1, metrics.columns);
      if (column < 0 || row < 0 || column + colSpan > metrics.columns) return null;
      return { row, column };
    },
    updateDragTargetFromPointer(event = null) {
      const point = event
        ? { x: event.clientX, y: event.clientY }
        : this.dragPointerPoint;
      if (!point || !this.dragGridMetrics) return false;
      const targetCell = this.getGridCellFromPoint(point.x, point.y);
      const sameTarget =
        this.dragTargetCell?.row == targetCell?.row &&
        this.dragTargetCell?.column == targetCell?.column &&
        this.dragPreviewLayout?.metrics === this.dragGridMetrics &&
        this.dragDropValid == Boolean(targetCell);
      if (sameTarget) return this.dragDropValid;
      const previousLayout = this.dragPreviewLayout || this.dragOriginalLayout;
      this.dragTargetCell = targetCell;
      const nextLayout = targetCell ? this.getDragPreviewLayout(targetCell) : null;
      const previewLayout = nextLayout || this.dragOriginalLayout;
      this.dragPreviewLayout = previewLayout;
      this.dragDropValid = Boolean(nextLayout);
      if (previewLayout) {
        this.applyPreviewLayoutToDom(previewLayout, this.dragWidgetId, 'drag');
        this.animateGridReflow(previousLayout, previewLayout, this.dragWidgetId);
      }
      return this.dragDropValid;
    },
    updateDragPosition(event) {
      if (!this.dragWidgetId || !event) return;
      this.dragPointerPoint = { x: event.clientX, y: event.clientY };
      this.updateDragTargetFromPointer(event);
      this.updateDragShadow(event);
      this.scheduleDragAutoScroll();
    },
    pointerMatchesDrag(event) {
      return this.dragPointerId === null || event.pointerId == this.dragPointerId;
    },
    flushPendingDragMove(event) {
      const latestEvent = this.dragMoveEvent;
      if (this.dragMoveFrame) cancelAnimationFrame(this.dragMoveFrame);
      this.dragMoveFrame = null;
      this.dragMoveEvent = null;
      if (latestEvent && this.isDragging) this.updateDragPosition(latestEvent);
      else if (
        event &&
        this.isDragging &&
        (!this.dragPointerPoint ||
          this.dragPointerPoint.x != event.clientX ||
          this.dragPointerPoint.y != event.clientY)
      ) {
        this.updateDragPosition(event);
      }
    },
    cancelPendingDragMove() {
      if (this.dragMoveFrame) cancelAnimationFrame(this.dragMoveFrame);
      this.dragMoveFrame = null;
      this.dragMoveEvent = null;
    },
    onWidgetPointerMove(event) {
      if (this.isResizing) {
        if (!this.pointerMatchesResize(event)) return;
        if (event.cancelable) event.preventDefault();
        this.resizeMoveEvent = event;
        if (this.resizeMoveFrame) return;
        this.resizeMoveFrame = requestAnimationFrame(() => {
          this.resizeMoveFrame = null;
          const latestEvent = this.resizeMoveEvent;
          this.resizeMoveEvent = null;
          if (latestEvent && this.isResizing) {
            this.resizePointerPoint = { x: latestEvent.clientX, y: latestEvent.clientY };
            this.updateResizeFromPointer();
          }
          if (this.resizeReleasePending && this.isResizing) {
            this.resizeReleasePending = false;
            this.finishWidgetResize(true);
          }
        });
        return;
      }
      if (!this.pointerMatchesDrag(event)) return;
      if (event.cancelable) event.preventDefault();
      this.dragMoveEvent = event;
      if (this.dragMoveFrame) return;
      this.dragMoveFrame = requestAnimationFrame(() => {
        this.dragMoveFrame = null;
        const latestEvent = this.dragMoveEvent;
        this.dragMoveEvent = null;
        if (latestEvent && this.isDragging) this.updateDragPosition(latestEvent);
      });
    },
    onWidgetPointerEnd(event) {
      if (this.isResizing) {
        if (!this.pointerMatchesResize(event)) return;
        if (this.resizeMoveFrame) {
          this.resizeMoveEvent = event;
          this.resizeReleasePending = true;
          return;
        }
        this.flushPendingResizeMove(event);
        this.finishWidgetResize(true);
        return;
      }
      if (!this.pointerMatchesDrag(event)) return;
      this.flushPendingDragMove(event);
      this.finishWidgetDrag(true);
    },
    onWidgetPointerCancel(event) {
      if (this.isResizing) {
        if (!this.pointerMatchesResize(event)) return;
        this.cancelPendingResizeMove();
        this.resizeReleasePending = false;
        this.finishWidgetResize(false);
        return;
      }
      if (!this.pointerMatchesDrag(event)) return;
      this.cancelPendingDragMove();
      this.finishWidgetDrag(false);
    },
    onDashboardKeyDown(event) {
      if (event.key == 'Escape') {
        event.preventDefault();
        if (this.isDragging) this.finishWidgetDrag(false);
        if (this.isResizing) this.finishWidgetResize(false);
      }
    },
    onDashboardWindowBlur() {
      if (this.isDragging) this.finishWidgetDrag(false);
      if (this.isResizing) this.finishWidgetResize(false);
    },
    getLayoutAnimationOffset(item, timestamp) {
      const progress = Math.min(1, Math.max(0, (timestamp - item.startedAt) / item.duration));
      const eased = 1 - Math.pow(1 - progress, 3);
      return {
        x: item.startX * (1 - eased),
        y: item.startY * (1 - eased),
        done: progress >= 1,
      };
    },
    getCurrentLayoutAnimationOffsets(timestamp = performance.now()) {
      return this._layoutAnimationItems.reduce((offsets, item) => {
        offsets[item.id] = this.getLayoutAnimationOffset(item, timestamp);
        return offsets;
      }, {});
    },
    clearGridReflowAnimation() {
      if (this._layoutAnimationFrame) cancelAnimationFrame(this._layoutAnimationFrame);
      this._layoutAnimationFrame = null;
      this._layoutAnimationToken += 1;
      this._layoutAnimationItems.forEach((item) => {
        if (!item.element) return;
        item.element.style.transform = '';
        item.element.style.willChange = '';
      });
      this._layoutAnimationItems = [];
    },
    animateGridReflow(previousLayout, nextLayout, activeWidgetId) {
      if (!previousLayout || !nextLayout) return;
      const previousById = previousLayout.byId || {};
      const nextById = nextLayout.byId || {};
      const affectedIds = new Set(nextLayout.affectedIds || []);
      Object.keys(nextById).forEach((id) => {
        if (
          previousById[id] &&
          (previousById[id].left != nextById[id].left ||
            previousById[id].top != nextById[id].top ||
            previousById[id].width != nextById[id].width ||
            previousById[id].height != nextById[id].height)
        ) {
          affectedIds.add(id);
        }
      });
      const currentOffsets = this.getCurrentLayoutAnimationOffsets();
      if (this._layoutAnimationFrame) cancelAnimationFrame(this._layoutAnimationFrame);
      this._layoutAnimationFrame = null;
      const previousItems = this._layoutAnimationItems;
      const token = ++this._layoutAnimationToken;
      const duration = 180;
      const records = [];

      affectedIds.forEach((id) => {
        if (id == activeWidgetId || !previousById[id] || !nextById[id]) return;
        const currentOffset = currentOffsets[id] || { x: 0, y: 0 };
        const startX = previousById[id].left - nextById[id].left + currentOffset.x;
        const startY = previousById[id].top - nextById[id].top + currentOffset.y;
        if (Math.abs(startX) < 0.5 && Math.abs(startY) < 0.5) return;
        records.push({ id, startX, startY });
      });

      if (!records.length) {
        previousItems.forEach((item) => {
          if (!item.element) return;
          item.element.style.transform = '';
          item.element.style.willChange = '';
        });
        this._layoutAnimationItems = [];
        return;
      }

      if (token != this._layoutAnimationToken || this._isDestroyed) return;
      previousItems.forEach((item) => {
        if (!item.element) return;
        item.element.style.transform = '';
        item.element.style.willChange = '';
      });
      const elementsById = this.getDashboardItemElementMap();
      const startedAt = performance.now();
      const items = records
        .map((record) => ({ ...record, element: elementsById[record.id], startedAt, duration }))
        .filter((item) => item.element);
      if (!items.length) return;
      this._layoutAnimationItems = items;
      items.forEach((item) => {
        item.element.style.willChange = 'transform';
        item.element.style.transform = `translate3d(${item.startX}px, ${item.startY}px, 0)`;
      });

      const step = (timestamp) => {
        if (token != this._layoutAnimationToken || this._isDestroyed) return;
        let complete = true;
        items.forEach((item) => {
          const offset = this.getLayoutAnimationOffset(item, timestamp);
          if (!offset.done) complete = false;
          item.element.style.transform = `translate3d(${offset.x}px, ${offset.y}px, 0)`;
        });
        if (complete) {
          items.forEach((item) => {
            item.element.style.transform = '';
            item.element.style.willChange = '';
          });
          this._layoutAnimationFrame = null;
          this._layoutAnimationItems = [];
          return;
        }
        this._layoutAnimationFrame = requestAnimationFrame(step);
      };
      this._layoutAnimationFrame = requestAnimationFrame(step);
    },
    getResizeGhostElement() {
      const element = this.$refs.resizePlaceholder;
      return Array.isArray(element) ? element[0] : element;
    },
    cancelResizeGhostAnimation() {
      if (this._resizeGhostAnimationFrame) cancelAnimationFrame(this._resizeGhostAnimationFrame);
      this._resizeGhostAnimationFrame = null;
      this._resizeGhostAnimationToken += 1;
      const element = this.getResizeGhostElement();
      if (element) {
        element.style.transform = '';
        element.style.transformOrigin = '';
      }
    },
    animateResizeGhost(previousRect = null, targetPlacement = null) {
      this.cancelResizeGhostAnimation();
      const token = this._resizeGhostAnimationToken;
      const startAnimation = () => {
        if (token != this._resizeGhostAnimationToken || this._isDestroyed) return;
        const element = this.getResizeGhostElement();
        if (!element) return;
        const targetWidth = targetPlacement?.width || 0;
        const targetHeight = targetPlacement?.height || 0;
        if (!targetWidth || !targetHeight) return;
        const startScaleX = previousRect?.width > 0 ? previousRect.width / targetWidth : 0.96;
        const startScaleY = previousRect?.height > 0 ? previousRect.height / targetHeight : 0.96;
        const startedAt = performance.now();
        const duration = 180;
        element.style.transformOrigin = 'top left';
        element.style.transform = `scale(${startScaleX}, ${startScaleY})`;

        const step = (timestamp) => {
          if (token != this._resizeGhostAnimationToken || this._isDestroyed) return;
          const progress = Math.min(1, Math.max(0, (timestamp - startedAt) / duration));
          const eased = 1 - Math.pow(1 - progress, 3);
          const scaleX = startScaleX + (1 - startScaleX) * eased;
          const scaleY = startScaleY + (1 - startScaleY) * eased;
          element.style.transform = `scale(${scaleX}, ${scaleY})`;
          if (progress >= 1) {
            element.style.transform = '';
            element.style.transformOrigin = '';
            this._resizeGhostAnimationFrame = null;
            return;
          }
          this._resizeGhostAnimationFrame = requestAnimationFrame(step);
        };
        this._resizeGhostAnimationFrame = requestAnimationFrame(step);
      };
      if (this.getResizeGhostElement()) startAnimation();
      else this.$nextTick(startAnimation);
    },
    hasDragCoordinateChanges() {
      return (this.dragPreviewLayout?.items || []).some((item) => {
        const original = this.dragOriginalLayout?.byId[item.id];
        return original &&
          (item.gridRow != original.gridRow || item.gridColumn != original.gridColumn);
      });
    },
    startWidgetResize(widgetId, event) {
      if (
        !event ||
        this.isDragging ||
        this.isResizing ||
        (event.pointerType == 'mouse' && event.button != 0)
      ) return false;
      this.flushPendingReleaseCommit();
      this.resizeReleasePending = false;
      const widget = this.widgetList.find((item) => item.id == widgetId);
      if (!widget) return false;
      if (event.cancelable) event.preventDefault();
      this.resizeGridMetrics = this.getDashboardGridMetrics();
      this.resizeInteractionBounds = this.getDashboardInteractionBounds();
      if (!this.resizeInteractionBounds) return false;
      this.resizeOriginalLayout = this.buildCoordinateLayout(
        this.currentGridLayout.items.map((item) => ({ ...item })),
        this.resizeGridMetrics,
      );
      this.cacheDashboardItemElements();
      this.resizeOriginalPlacement = this.resizeOriginalLayout.byId[widgetId];
      if (!this.resizeOriginalPlacement) return false;
      this.resizeOriginalSpans = {
        rowSpan: this.normalizeWidgetRowSpan(widget.rowSpan),
        colSpan: this.normalizeWidgetColSpan(widget.colSpan),
      };
      this.resizePreviewSpans = { ...this.resizeOriginalSpans };
      this.resizeWidgetId = widgetId;
      this.resizePointerId = event.pointerId;
      const resizeElement = this.getDashboardItemElementMap()[widgetId];
      const resizeHandle = resizeElement?.querySelector('.widget-resize-handle');
      this.resizePointerCaptureTarget = this.captureWidgetPointer(event, resizeHandle);
      this.resizeStartPoint = { x: event.clientX, y: event.clientY };
      this.resizePointerPoint = { x: event.clientX, y: event.clientY };
      this.resizeStartScrollTop = this.$refs.dashboardMain?.scrollTop || 0;
      this.resizeDropValid = true;
      this._resizeGhostSize = {
        width: this.resizeOriginalPlacement.width,
        height: this.resizeOriginalPlacement.height,
      };
      this.resizePreviewLayout = this.getResizePreviewLayout(this.resizePreviewSpans);
      this.isResizing = true;
      if (this.resizePreviewLayout) {
        this.applyPreviewLayoutToDom(this.resizePreviewLayout, this.resizeWidgetId, 'resize');
      }
      this.updateResizeFromPointer();
      return true;
    },
    onWidgetResizeStart(widgetId, event) {
      if (!this.startWidgetResize(widgetId, event)) return;
      window.addEventListener('pointermove', this.onWidgetPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onWidgetPointerEnd);
      window.addEventListener('pointercancel', this.onWidgetPointerCancel);
      window.addEventListener('keydown', this.onDashboardKeyDown);
      window.addEventListener('blur', this.onDashboardWindowBlur);
    },
    pointerMatchesResize(event) {
      return this.resizePointerId === null || event.pointerId == this.resizePointerId;
    },
    getResizePreviewLayout(spans) {
      if (!this.resizeOriginalPlacement || !this.resizeGridMetrics) return null;
      const maxColSpan = Math.max(1, this.resizeGridMetrics.columns - this.resizeOriginalPlacement.gridColumn);
      const resized = {
        ...this.resizeOriginalPlacement,
        rowSpan: this.normalizeWidgetRowSpan(spans.rowSpan),
        colSpan: Math.min(this.normalizeWidgetColSpan(spans.colSpan), maxColSpan),
      };
      const compacted = this.compactResolvedLayout(
        [
          resized,
          ...this.resizeOriginalLayout.items.filter((item) => item.id != this.resizeWidgetId),
        ],
        this.resizeGridMetrics,
        this.resizeWidgetId,
      );
      const affectedIds = compacted
        .filter((item) => {
          const original = this.resizeOriginalLayout.items.find((candidate) => candidate.id == item.id);
          return (
            original &&
            (original.gridRow != item.gridRow || original.gridColumn != item.gridColumn)
          );
        })
        .map((item) => item.id);
      return this.buildCoordinateLayout(compacted, this.resizeGridMetrics, { affectedIds });
    },
    updateResizeFromPointer() {
      if (!this.isResizing || !this.resizeStartPoint || !this.resizeGridMetrics) return;
      const point = this.resizePointerPoint;
      if (!point) return;
      this.scheduleResizeAutoScroll();
      const previousLayout = this.resizePreviewLayout || this.resizeOriginalLayout;
      const columnPitch = this.resizeGridMetrics.columnWidth + this.resizeGridMetrics.columnGap;
      const rowPitch = this.resizeGridMetrics.rowHeight + this.resizeGridMetrics.rowGap;
      const columnDelta = columnPitch > 0 ? Math.round((point.x - this.resizeStartPoint.x) / columnPitch) : 0;
      const scrollDelta = (this.$refs.dashboardMain?.scrollTop || 0) - this.resizeStartScrollTop;
      const rowDelta = rowPitch > 0
        ? Math.round((point.y - this.resizeStartPoint.y + scrollDelta) / rowPitch)
        : 0;
      const nextSpans = {
        colSpan: Math.max(1, Math.min(2, this.resizeOriginalSpans.colSpan + columnDelta)),
        rowSpan: Math.max(1, Math.min(2, this.resizeOriginalSpans.rowSpan + rowDelta)),
      };
      const pointerCanvasBottom = this.getResizePointerCanvasY();
      const currentPreviewHeight = this.resizePreviewLayout?.height || this.resizeOriginalLayout?.height || 0;
      if (pointerCanvasBottom > currentPreviewHeight) {
        this.syncDashboardCanvasHeight(pointerCanvasBottom, this.resizeGridMetrics);
      }
      if (
        this.resizePreviewLayout &&
        this.resizePreviewLayout.metrics === this.resizeGridMetrics &&
        this.resizePreviewSpans?.colSpan == nextSpans.colSpan &&
        this.resizePreviewSpans?.rowSpan == nextSpans.rowSpan
      ) {
        return;
      }
      const nextLayout = this.getResizePreviewLayout(nextSpans);
      if (!nextLayout) return;
      this.resizePreviewSpans = nextSpans;
      this.resizeDropValid = true;
      this.resizePreviewLayout = nextLayout;
      const nextPlacement = nextLayout.byId[this.resizeWidgetId];
      this.applyPreviewLayoutToDom(nextLayout, this.resizeWidgetId, 'resize');
      this.animateGridReflow(previousLayout, nextLayout, this.resizeWidgetId);
      this.animateResizeGhost(this._resizeGhostSize, nextPlacement);
      this._resizeGhostSize = nextPlacement
        ? { width: nextPlacement.width, height: nextPlacement.height }
        : this._resizeGhostSize;
    },
    flushPendingResizeMove(event) {
      const latestEvent = this.resizeMoveEvent;
      if (this.resizeMoveFrame) cancelAnimationFrame(this.resizeMoveFrame);
      this.resizeMoveFrame = null;
      this.resizeMoveEvent = null;
      const point = latestEvent || event;
      if (
        point &&
        this.isResizing &&
        (!this.resizePointerPoint ||
          this.resizePointerPoint.x != point.clientX ||
          this.resizePointerPoint.y != point.clientY)
      ) {
        this.resizePointerPoint = { x: point.clientX, y: point.clientY };
        this.updateResizeFromPointer();
      }
    },
    cancelPendingResizeMove() {
      if (this.resizeMoveFrame) cancelAnimationFrame(this.resizeMoveFrame);
      this.resizeMoveFrame = null;
      this.resizeMoveEvent = null;
    },
    hasResizeChanges() {
      return (this.resizePreviewLayout?.items || []).some((item) => {
        const original = this.resizeOriginalLayout?.byId[item.id];
        if (!original) return false;
        const moved = item.gridRow != original.gridRow || item.gridColumn != original.gridColumn;
        const resized = item.id == this.resizeWidgetId &&
          (this.resizePreviewSpans?.rowSpan != this.resizeOriginalSpans.rowSpan ||
            this.resizePreviewSpans?.colSpan != this.resizeOriginalSpans.colSpan);
        return moved || resized;
      });
    },
    commitPreviewLayout(layout, activeWidgetId = null, commitSpans = false, spanOverride = null) {
      if (!layout) return false;
      const nextWidgets = this.widgetList.map((widget) => {
        const placement = layout.byId[widget.id];
        if (!placement) return widget;
        const committedSpans = spanOverride && widget.id == activeWidgetId ? spanOverride : placement;
        const spansChanged = commitSpans && widget.id == activeWidgetId &&
          (widget.rowSpan != committedSpans.rowSpan || widget.colSpan != committedSpans.colSpan);
        if (
          widget.gridRow == placement.gridRow &&
          widget.gridColumn == placement.gridColumn &&
          !spansChanged
        ) {
          return widget;
        }
        return {
          ...widget,
          gridRow: placement.gridRow,
          gridColumn: placement.gridColumn,
          ...(commitSpans && widget.id == activeWidgetId
            ? { rowSpan: committedSpans.rowSpan, colSpan: committedSpans.colSpan }
            : {}),
        };
      });
      const changed = nextWidgets.some((widget, index) => {
        const previous = this.widgetList[index];
        return widget.gridRow != previous.gridRow ||
          widget.gridColumn != previous.gridColumn ||
          widget.rowSpan != previous.rowSpan ||
          widget.colSpan != previous.colSpan;
      });
      if (changed) this.widgetList = nextWidgets;
      return changed;
    },
    finishWidgetResize(applyResize) {
      this.resizeReleasePending = false;
      if (this._releaseCommitPending?.kind == 'resize') this.cancelPendingReleaseCommit();
      this.cancelPendingResizeMove();
      this.cancelResizeAutoScroll();
      const validResize = Boolean(this.isResizing && applyResize && this.resizeDropValid && this.resizePreviewLayout);
      const previousLayout = this.resizePreviewLayout;
      const finalLayout = validResize ? this.resizePreviewLayout : this.resizeOriginalLayout;
      const changed = validResize ? this.hasResizeChanges() : false;
      const shouldCommit = validResize && changed;
      const resizeWidgetId = this.resizeWidgetId;
      const resizePointerId = this.resizePointerId;
      if (shouldCommit) {
        this.removeDragListeners();
        this.cancelResizeGhostAnimation();
        this.releaseWidgetPointer(this.resizePointerCaptureTarget, resizePointerId);
        this.scheduleReleaseCommit({
          kind: 'resize',
          layout: finalLayout,
          activeWidgetId: resizeWidgetId,
          commitSpans: true,
          spans: this.resizePreviewSpans ? { ...this.resizePreviewSpans } : null,
        });
        return;
      }
      if (!validResize && finalLayout) this.applyPreviewLayoutToDom(finalLayout, this.resizeWidgetId, 'resize');
      if (!validResize && previousLayout && finalLayout) {
        this.animateGridReflow(previousLayout, finalLayout, this.resizeWidgetId);
      }
      this.removeDragListeners();
      this.cancelResizeGhostAnimation();
      this.releaseWidgetPointer(this.resizePointerCaptureTarget, resizePointerId);
      this.resizeWidgetId = null;
      this.resizePointerId = null;
      this.resizePointerCaptureTarget = null;
      this.resizePointerPoint = null;
      this.resizeStartPoint = null;
      this.resizeStartScrollTop = 0;
      this.resizeGridMetrics = null;
      this.resizeInteractionBounds = null;
      this.resizePreviewLayout = null;
      this.resizeOriginalPlacement = null;
      this.resizeOriginalLayout = null;
      this.resizeOriginalSpans = null;
      this.resizePreviewSpans = null;
      this.resizeDropValid = false;
      this.syncDashboardCanvasHeight(finalLayout?.height, finalLayout?.metrics);
      this.isResizing = false;
      this._resizeGhostSize = null;
      this._dashboardItemElementsById = null;
      this._previewAppliedLayout = null;
    },
    removeDragListeners() {
      window.removeEventListener('pointermove', this.onWidgetPointerMove);
      window.removeEventListener('pointerup', this.onWidgetPointerEnd);
      window.removeEventListener('pointercancel', this.onWidgetPointerCancel);
      window.removeEventListener('keydown', this.onDashboardKeyDown);
      window.removeEventListener('blur', this.onDashboardWindowBlur);
    },
    finishWidgetDrag(applyDrop) {
      this.cancelPendingDragMove();
      const validDrop = Boolean(this.isDragging && applyDrop && this.dragDropValid && this.dragPreviewLayout);
      const previousLayout = this.dragPreviewLayout;
      const finalLayout = validDrop ? this.dragPreviewLayout : this.dragOriginalLayout;
      const changed = validDrop ? this.hasDragCoordinateChanges() : false;
      const shouldCommit = validDrop && changed;
      const dragPointerId = this.dragPointerId;
      if (shouldCommit) {
        this.removeDragListeners();
        this.releaseWidgetPointer(this.dragPointerCaptureTarget, dragPointerId);
        this.removeDragShadow();
        if (this.dragScrollFrame) {
          cancelAnimationFrame(this.dragScrollFrame);
          this.dragScrollFrame = null;
        }
        this.scheduleReleaseCommit({
          kind: 'drag',
          layout: finalLayout,
          activeWidgetId: null,
          commitSpans: false,
        });
        return;
      }
      if (!validDrop && finalLayout) this.applyPreviewLayoutToDom(finalLayout, this.dragWidgetId, 'drag');
      if (!validDrop && previousLayout && finalLayout) {
        this.animateGridReflow(previousLayout, finalLayout, this.dragWidgetId);
      }
      this.removeDragListeners();
      this.releaseWidgetPointer(this.dragPointerCaptureTarget, dragPointerId);
      this.dragWidgetId = null;
      this.dragPointerId = null;
      this.dragPointerCaptureTarget = null;
      this.dragPointerPoint = null;
      this.dragGridMetrics = null;
      this.dragInteractionBounds = null;
      this.dragOriginalLayout = null;
      this.dragPreviewLayout = null;
      this.dragOriginalPlacement = null;
      this.dragTargetCell = null;
      this.dragDropValid = false;
      this.syncDashboardCanvasHeight(finalLayout?.height, finalLayout?.metrics);
      this.isDragging = false;
      this._dashboardItemElementsById = null;
      this._previewAppliedLayout = null;
      this.removeDragShadow();
      if (this.dragScrollFrame) {
        cancelAnimationFrame(this.dragScrollFrame);
        this.dragScrollFrame = null;
      }
    },
    scheduleDragAutoScroll() {
      if (!this.isDragging || !this.$refs.dashboardMain || this.dragScrollFrame) return;
      this.dragScrollFrame = requestAnimationFrame(this.runDragAutoScroll);
    },
    runDragAutoScroll() {
      this.dragScrollFrame = null;
      const container = this.$refs.dashboardMain;
      const point = this.dragPointerPoint;
      if (!this.isDragging || !container || !point) return;

      const bounds = this.dragInteractionBounds || this.getDashboardInteractionBounds();
      if (!bounds) return;
      const rect = { top: bounds.mainTop, bottom: bounds.mainBottom };
      const edgeSize = 80;
      let scrollAmount = 0;
      if (point.y < rect.top + edgeSize) {
        scrollAmount = -Math.max(4, edgeSize - (point.y - rect.top)) / 2;
      } else if (point.y > rect.bottom - edgeSize) {
        scrollAmount = Math.max(4, edgeSize - (rect.bottom - point.y)) / 2;
      }
      if (!scrollAmount) return;

      const previousScrollTop = container.scrollTop;
      container.scrollTop += scrollAmount;
      if (container.scrollTop == previousScrollTop) return;

      if (this.dragPointerPoint) this.updateDragTargetFromPointer();
      this.scheduleDragAutoScroll();
    },

    scheduleResizeAutoScroll() {
      if (!this.isResizing || !this.$refs.dashboardMain || this.resizeScrollFrame) return;
      this.resizeScrollFrame = requestAnimationFrame(this.runResizeAutoScroll);
    },
    cancelResizeAutoScroll() {
      if (this.resizeScrollFrame) cancelAnimationFrame(this.resizeScrollFrame);
      this.resizeScrollFrame = null;
    },
    runResizeAutoScroll() {
      this.resizeScrollFrame = null;
      const container = this.$refs.dashboardMain;
      const point = this.resizePointerPoint;
      if (!this.isResizing || !container || !point) return;
      const rect = container.getBoundingClientRect();
      const edgeSize = 80;
      let scrollAmount = 0;
      if (point.y < rect.top + edgeSize) {
        scrollAmount = -Math.max(4, edgeSize - (point.y - rect.top)) / 2;
      } else if (point.y > rect.bottom - edgeSize) {
        scrollAmount = Math.max(4, edgeSize - (rect.bottom - point.y)) / 2;
      }
      if (!scrollAmount) return;
      const previousScrollTop = container.scrollTop;
      container.scrollTop += scrollAmount;
      if (container.scrollTop == previousScrollTop) return;
      this.updateResizeFromPointer();
      this.scheduleResizeAutoScroll();
    },

    createDragShadow(widgetId, event) {
      this.removeDragShadow();
      const widgetEl = this.getDashboardItemElements().find((el) => el.dataset.widgetId == widgetId);
      if (!widgetEl || !event) return;

      const rect = widgetEl.getBoundingClientRect();
      const title = widgetEl.querySelector('.widget-title-text')?.innerText?.trim() || 'Moving widget';
      const icon = widgetEl.querySelector('.header-container .title > img')?.cloneNode(true);
      const shadow = document.createElement('div');
      shadow.className = 'dashboard-drag-shadow';
      const headerEl = document.createElement('div');
      headerEl.setAttribute(
        'style',
        [
          'display: flex',
          'align-items: center',
          'gap: 8px',
          'height: 2.25rem',
          'min-height: 2.25rem',
          'padding: 0.25rem 0.5rem',
          'border-bottom: 1px solid lightgray',
          'box-sizing: border-box',
        ].join(';'),
      );
      if (icon) {
        icon.style.width = '24px';
        icon.style.height = '24px';
        icon.style.objectFit = 'contain';
        headerEl.appendChild(icon);
      }
      const titleEl = document.createElement('div');
      titleEl.textContent = title;
      titleEl.style.overflow = 'hidden';
      titleEl.style.textOverflow = 'ellipsis';
      titleEl.style.whiteSpace = 'nowrap';
      headerEl.appendChild(titleEl);
      shadow.appendChild(headerEl);
      shadow.setAttribute(
        'style',
        [
          'position: fixed',
          'top: 0',
          'left: 0',
          'z-index: 9999',
          'pointer-events: none',
          `width: ${rect.width}px`,
          `height: ${rect.height}px`,
          'border: 1px solid #9daacc',
          'background: rgba(255, 255, 255, 0.92)',
          'font-size: 0.75rem',
          'font-weight: 600',
          'color: #2d3348',
          'box-sizing: border-box',
          'overflow: hidden',
          'transform: translate3d(-9999px, -9999px, 0)',
          'will-change: transform',
        ].join(';'),
      );
      document.body.appendChild(shadow);
      this.dragShadowEl = shadow;
      this.dragShadowOffset = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };

      this.updateDragShadow(event);
    },

    updateDragShadow(event) {
      if (!this.dragShadowEl || !event) return;
      const clientX = event.clientX;
      const clientY = event.clientY;
      if (clientX === undefined || clientY === undefined) return;

      this.dragShadowPoint = { x: clientX, y: clientY };
      if (this.dragShadowFrame) return;

      this.dragShadowFrame = requestAnimationFrame(() => {
        if (this.dragShadowEl && this.dragShadowPoint) {
          const offset = this.dragShadowOffset || { x: 0, y: 0 };
          this.dragShadowEl.style.transform = `translate3d(${this.dragShadowPoint.x - offset.x}px, ${
            this.dragShadowPoint.y - offset.y
          }px, 0)`;
        }
        this.dragShadowFrame = null;
      });
    },

    removeDragShadow() {
      if (this.dragShadowFrame) {
        cancelAnimationFrame(this.dragShadowFrame);
        this.dragShadowFrame = null;
      }
      if (this.dragShadowEl && this.dragShadowEl.parentNode) {
        this.dragShadowEl.parentNode.removeChild(this.dragShadowEl);
      }
      this.dragShadowEl = null;
      this.dragShadowPoint = null;
      this.dragShadowOffset = null;
    },

    async getDashboardAccess() {
      const result = await this.$apiCallV2('/Dashboard/GetDashboardAccess', {});
      if (this._isDestroyed) return;
      if (!result || result?.[0]?.Results == '1') {
        return;
      }
      this.dashboardAccess = result.map((item) => item.Control);
    },
  },

  async mounted() {
    if (this.dashboardSasEnabled) await this.getDashboardAccess();
    if (this._isDestroyed) return;
    if (!this.$store.state.dashboard) await this.getDashboard();
    if (this._isDestroyed) return;
    this.widgetList = this.$store.state.dashboard || [];
    const migration = this.migrateLegacyCoordinates(this.widgetList);
    if (migration.migrated) {
      this.widgetList = migration.widgets;
      this.$store.commit('updateDashboard', this.widgetList);
      this.saveDashboard();
    }

    window.addEventListener('beforeunload', this.handleUnload);
    window.addEventListener('resize', this.handleDashboardResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.handleVisualViewportResize);
    }
    this.$nextTick(() => {
      if (this._isDestroyed) return;
      this.handleDashboardResize();
      if (this.compactWidgetCoordinates()) this.saveDashboard();
      if (typeof ResizeObserver == 'function' && this.$refs.dashboardMain) {
        this.dashboardResizeObserver = new ResizeObserver(this.handleDashboardResize);
        this.dashboardResizeObserver.observe(this.$refs.dashboardMain);
      }
    });
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleUnload);
    window.removeEventListener('resize', this.handleDashboardResize);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleVisualViewportResize);
    }
    if (this.dashboardResizeObserver) {
      this.dashboardResizeObserver.disconnect();
      this.dashboardResizeObserver = null;
    }
    this.flushPendingReleaseCommit(true);
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
    this.cancelWidgetScroll();
    this.cancelResizeAutoScroll();
    this.finishWidgetDrag(false);
    this.finishWidgetResize(false);
    this.clearGridReflowAnimation();
    this.cancelResizeGhostAnimation();
  },
  created() {
    this.dragPointerPoint = null;
    this.dragScrollFrame = null;
    this.dragShadowFrame = null;
    this.dragShadowPoint = null;
    this.dragShadowOffset = null;
    this.dragMoveFrame = null;
    this.dragMoveEvent = null;
    this.dragPointerCaptureTarget = null;
    this.resizePointerPoint = null;
    this.resizeStartPoint = null;
    this.resizeMoveFrame = null;
    this.resizeMoveEvent = null;
    this.resizePointerCaptureTarget = null;
    this.dragPreviewLayout = null;
    this.dragTargetCell = null;
    this.resizePreviewLayout = null;
    this.resizePreviewSpans = null;
    this._dashboardItemElementsById = null;
    this._previewDomTickPending = false;
    this._previewAppliedLayout = null;
    this._releaseCommitPending = null;
    this._releaseCommitFrame = null;
    this._releaseCommitTimer = null;
    this._resizeGhostSize = null;
    this._layoutAnimationFrame = null;
    this._layoutAnimationToken = 0;
    this._layoutAnimationItems = [];
    this._resizeGhostAnimationFrame = null;
    this._resizeGhostAnimationToken = 0;
    const { dashboard } = dashboardItemConfig(this.$router, {
      apiCallV2: this.$apiCallV2.bind(this),
      fullScreenLoading: this.$fullscreenLoading.bind(this),
    });
    this.dashboardItemConfigList = dashboard;
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.dashboard-main {
  width: 100%;
  max-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  box-sizing: border-box;
  padding-right: 1rem !important;
  padding-top: 0 !important;

  @media screen and (min-width: 768px) {
    padding-right: 1.5rem !important;
  }

  @media screen and (min-width: 1200px) {
    padding-right: 2rem !important;
  }
}

.dashboard-header {
  width: 100%;
  padding-bottom: 0.75rem;
  padding-top: 12px;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 2;
  background: linear-gradient(180deg, var(--main-bg-color) 25%, transparent 100%);

  .add-widget-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  font-size: 0.9rem;
}

.dashboard-list-wrapper {
  position: relative;
  flex: 0 0 auto;
  min-height: 0;

  &.is-empty {
    flex: 1 1 auto;

    .dashboard-list {
      height: 100%;
      grid-auto-rows: 1fr;
    }
  }
}

.dashboard-list {
  width: 100%;
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: 19.5rem;
  grid-auto-flow: row;
  gap: 0.85rem;
  min-height: 100%;
  align-content: start;
  padding-bottom: 0.5rem;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (min-width: 1920px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media screen and (min-width: 2560px) {
    grid-template-columns: repeat(auto-fill, minmax(30rem, 1fr));
  }

  .dashboard-grid-item {
    position: absolute;
    min-width: 0;
    min-height: 0;
  }

  .dashboard-grid-item-dragging {
    display: none;
  }

  .dashboard-grid-item-resizing {
    display: none;
  }

  .dashboard-drag-placeholder {
    position: absolute;
    z-index: 1;
    box-sizing: border-box;
    border: 2px dashed #6c8bfd;
    border-radius: 2px;
    background: rgba(108, 139, 253, 0.1);
    pointer-events: none;
  }

  .dashboard-resize-placeholder {
    position: absolute;
    z-index: 2;
    box-sizing: border-box;
    border: 2px solid rgba(108, 139, 253, 0.85);
    border-radius: 2px;
    background: rgba(108, 139, 253, 0.14);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.7);
    pointer-events: none;
    will-change: transform;

    span {
      position: absolute;
      right: 0.35rem;
      bottom: 0.35rem;
      padding: 0.15rem 0.3rem;
      border-radius: 2px;
      background: rgba(45, 51, 72, 0.78);
      color: white;
      font-size: 0.65rem;
      line-height: 1;
    }
  }

  .dashboard-widget-add-highlight {
    outline: 2px solid #6c8bfd;
    outline-offset: 2px;
  }

}

.dashboard-empty-placeholder {
  grid-column: 1 / -1;
  flex: 1 0 100%;
  max-width: none !important;
}

.dashboard-json-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 1rem 0 0.5rem;

  .dashboard-json-button {
    padding: 0.35rem 0.75rem;
  }

  input {
    display: none;
  }
}

@media (max-width: 600px) {
  div {
    font-size: inherit;
  }
}

</style>
